import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import $api from 'http'
import Notification from 'utils/Notifications'
import { getSubmissionsForReview } from 'service/AssignmentsService'
import {
  PageHeader,
  UnvButton,
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  DataTable,
} from 'components'

import styles from './TeacherDashboard.module.scss'

const fmtTime = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

const fmtDate = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return ''
  }
}

const todayKey = () => new Date().toISOString().slice(0, 10)

const TeacherDashboard = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  // Wave 4: title в табе браузера для главной страницы преподавателя.
  useEffect(() => {
    document.title = `${t('teacherDash.teacher', 'Преподаватель')} — ЧУ`
  }, [t])
  const [me, setMe] = useState(null)
  const [lessons, setLessons] = useState([])
  const [reviewQueue, setReviewQueue] = useState([])
  const [statements, setStatements] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const loadAll = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const [meRes, lessRes, subRes, stRes] = await Promise.allSettled([
        $api.get('/admin-panel/me/'),
        $api.get('/academic/lessons/my-week/', { params: { days: 7 } }),
        getSubmissionsForReview(),
        $api.get('/gradebook/statements/', { params: { status: 'draft' } }),
      ])
      setMe(meRes.status === 'fulfilled' ? meRes.value.data : null)
      setLessons(lessRes.status === 'fulfilled' ? (lessRes.value.data?.results || lessRes.value.data || []) : [])
      setReviewQueue(subRes.status === 'fulfilled' ? (subRes.value.data?.results || subRes.value.data || []) : [])

      let st = stRes.status === 'fulfilled' ? (stRes.value.data?.results || stRes.value.data || []) : []
      try {
        const inProg = await $api.get('/gradebook/statements/', { params: { status: 'in_progress' } })
        const more = inProg.data?.results || inProg.data || []
        st = [...st, ...more]
      } catch {
        
      }
      setStatements(st)
    } catch {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('teacherDash.loading_error'), type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const todayLessons = useMemo(() => {
    const k = todayKey()
    return (lessons || [])
      .filter((l) => {
        const d = l.date || (l.start_at ? l.start_at.slice(0, 10) : null)
        return d === k
      })
      .sort((a, b) => (a.start_at || '').localeCompare(b.start_at || ''))
  }, [lessons])

  const weekLoad = useMemo(() => (lessons || []).length, [lessons])

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton variant="card" />
        <div className={styles.grid}>
          <Skeleton variant="card" />
          <Skeleton variant="table" rows={5} cols={3} />
          <Skeleton variant="table" rows={5} cols={4} />
          <Skeleton variant="card" />
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <ErrorState title={t('teacherDash.loading_error')} onRetry={loadAll} />
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    )
  }

  const subtitle = `${t('teacherDash.department')}: ${me?.department || me?.department_name || '—'} · ${t('teacherDash.role')}: ${me?.primary_role || '—'}`

  return (
    <div className={styles.page}>
      <PageHeader
        title={me?.full_name || t('teacherDash.teacher')}
        subtitle={subtitle}
        actions={
          <div className={styles.quick}>
            <UnvButton variant="ghost" iconLeft="calendar" onClick={() => navigate('/schedule/')}>
              {t('teacherDash.schedule')}
            </UnvButton>
            <UnvButton variant="ghost" iconLeft="book" onClick={() => navigate('/assignments-list/')}>
              {t('teacherDash.assignments_btn')}
            </UnvButton>
            <UnvButton variant="ghost" iconLeft="file" onClick={() => navigate('/statements-list/')}>
              {t('teacherDash.statements_btn')}
            </UnvButton>
          </div>
        }
      />

      <div className={styles.grid}>
        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('common.today')}</h3>
            <span className={styles.muted}>{new Date().toLocaleDateString('ru-RU')}</span>
          </div>
          {todayLessons.length === 0 ? (
            <EmptyState iconName="calendar" title={t('teacherDash.no_lessons_today')} variant="compact" />
          ) : (
            <ul className={styles.lessonList}>
              {todayLessons.map((l) => (
                <li key={l.id} className={styles.lessonItem}>
                  <div className={styles.lessonTime}>
                    {fmtTime(l.start_at)}–{fmtTime(l.end_at)}
                  </div>
                  <div className={styles.lessonBody}>
                    <div className={styles.lessonSubject}>{l.subject_name || l.subject?.name || '—'}</div>
                    <div className={styles.lessonMeta}>
                      <span>{t('teacherDash.group')} {l.group_name || l.group?.name || '—'}</span>
                      <span>{t('teacherDash.auditorium')} {l.room || l.classroom || '—'}</span>
                    </div>
                  </div>
                  <UnvButton
                    variant="primary"
                    size="sm"
                    iconLeft="edit"
                    onClick={() => navigate(`/journal/?lesson=${l.id}`)}
                  >
                    {t('teacherDash.open_journal')}
                  </UnvButton>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('teacherDash.review_queue')}</h3>
            <UnvButton variant="ghost" size="sm" onClick={() => navigate('/assignments-list/')}>
              {t('teacherDash.all_btn')}
            </UnvButton>
          </div>
          {reviewQueue.length === 0 ? (
            <EmptyState iconName="check" title={t('teacherDash.no_review_works')} variant="compact" />
          ) : (
            <DataTable
              columns={[
                { key: 'student', label: t('teacherDash.student'), render: (_, r) => r.student_name || r.student?.full_name || '—' },
                { key: 'assignment', label: t('teacherDash.assignment'), render: (_, r) => r.assignment_title || r.assignment?.title || '—' },
                { key: 'submitted_at', label: t('teacherDash.submitted_at'), render: (v) => fmtDate(v) },
              ]}
              data={reviewQueue.slice(0, 8)}
              searchable={false}
              pageSize={8}
              emptyText={t('teacherDash.no_review_works')}
            />
          )}
        </div>

        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('teacherDash.statements_to_close')}</h3>
            <UnvButton variant="ghost" size="sm" onClick={() => navigate('/statements-list/')}>
              {t('teacherDash.all_btn')}
            </UnvButton>
          </div>
          {statements.length === 0 ? (
            <EmptyState iconName="file" title={t('teacherDash.no_open_statements')} variant="compact" />
          ) : (
            <DataTable
              columns={[
                { key: 'subject', label: t('teacherDash.subject'), render: (_, r) => r.subject_name || r.subject?.name || '—' },
                { key: 'group', label: t('teacherDash.group'), render: (_, r) => r.group_name || r.group?.name || '—' },
                { key: 'semester', label: t('teacherDash.semester'), render: (_, r) => r.semester_name || r.semester || '—' },
                {
                  key: 'status',
                  label: t('teacherDash.status'),
                  render: (v) => {
                    const variant = v === 'draft' ? 'warning' : v === 'in_progress' ? 'info' : v === 'closed' ? 'success' : 'muted'
                    return <Badge variant={variant} dot>{v}</Badge>
                  },
                },
              ]}
              data={statements.slice(0, 6)}
              searchable={false}
              pageSize={6}
              emptyText={t('teacherDash.no_open_statements')}
            />
          )}
        </div>

        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('teacherDash.my_load')}</h3>
          </div>
          <div className={styles.loadStats}>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{weekLoad}</div>
              <div className={styles.statLabel}>{t('teacherDash.lessons_week')}</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{todayLessons.length}</div>
              <div className={styles.statLabel}>{t('teacherDash.today_lower')}</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{reviewQueue.length}</div>
              <div className={styles.statLabel}>{t('teacherDash.in_review')}</div>
            </div>
          </div>
        </div>
      </div>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default TeacherDashboard
